import { useGameStore } from "../../store/gameStore";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Clock, LogIn } from "lucide-react";

function formatClock(minutes, day) {
  const total = ((minutes % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = Math.floor(total % 60);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `Day ${day} — ${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

/** Player-facing Free Roam HUD: the campaign clock, and accept/decline/late-join prompts for
 * fights that break out (via attacks or being spotted). Always mounted; renders nothing when
 * the GM hasn't turned Free Roam on for this table — except in a Raid, where the Master GM's
 * shared campaign clock is always visible so every Branch stays in sync on the time. */
export default function FreeRoamHud({ gameId, character, myMember, isGm, sidebarCollapsed, isRaid }) {
  const freeRoam = useGameStore((s) => s.free_roam);

  if (!freeRoam || (!freeRoam.enabled && !isRaid)) return null;

  const myMemberId = myMember?.id;
  const otherEncounters = (freeRoam.encounters || []).filter((e) => !e.order?.some((o) => o.member_id === myMemberId));
  // For the GM this sits in the same left column as GmSidebar + SceneTabBar — offset past both
  // (stacked below the scene tabs) so none of the three overlap/intercept each other's clicks.
  // Players have no sidebar, so their HUD stays in its original top-left corner.
  const leftClass = isGm ? (sidebarCollapsed ? "left-16" : "left-[352px]") : "left-4";
  const topClass = isGm ? "top-16" : "top-4";

  const respond = async (encounterId, accept) => {
    try {
      await api.post(`/games/${gameId}/free-roam/encounters/${encounterId}/respond`, { member_id: myMemberId, character_id: character?.id, name: character?.name || myMember?.display_name, accept });
    } catch {
      toast.error("Could not respond");
    }
  };

  const joinLate = async (encounterId) => {
    try {
      await api.post(`/games/${gameId}/free-roam/encounters/${encounterId}/join`, { member_id: myMemberId, character_id: character?.id, name: character?.name || myMember?.display_name });
      toast.success("Jumping into the fight!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join");
    }
  };

  return (
    <div className={`absolute ${topClass} ${leftClass} z-20 flex flex-col gap-2 items-start transition-[left] duration-200`} data-testid="free-roam-hud">
      <div className="px-3 h-9 rounded-full bg-black/60 backdrop-blur-2xl border border-white/10 flex items-center gap-2 text-xs" data-testid="free-roam-clock-badge">
        <Clock className="w-3.5 h-3.5 text-[#D4AF37]" /> {formatClock(freeRoam.clock_minutes, freeRoam.clock_day)}
        {freeRoam.pace === "hustle" && <span className="text-[#D90429] uppercase text-[10px]">Hustling</span>}
      </div>

      {!isGm && otherEncounters.map((enc) => {
        const invited = enc.invited_member_ids?.includes(myMemberId);
        return (
          <div key={enc.id} className="px-3 py-2 rounded-xl bg-black/70 backdrop-blur-2xl border border-[#D90429]/40 text-xs space-y-1.5" data-testid={`free-roam-invite-${enc.id}`}>
            <p className="text-[#f1e0c5]/70">A fight has broken out nearby.</p>
            {invited ? (
              <div className="flex gap-1.5">
                <Button data-testid={`accept-fight-${enc.id}`} size="sm" onClick={() => respond(enc.id, true)} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] text-[10px] h-7 px-3">Join In</Button>
                <Button data-testid={`decline-fight-${enc.id}`} size="sm" variant="secondary" onClick={() => respond(enc.id, false)} className="rounded-full text-[10px] h-7 px-3">Sit Out</Button>
              </div>
            ) : (
              <Button data-testid={`join-late-${enc.id}`} size="sm" variant="secondary" onClick={() => joinLate(enc.id)} className="rounded-full gap-1.5 text-[10px] h-7 px-3">
                <LogIn className="w-3 h-3" /> Jump In (joins at the back)
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
